import { useEffect } from "react";
//my imports
import { retreivePyramidSnapshot, retrieveHistory, retrieveLastVisitedGameMode, retrieveLastVisitedLevelID, retrieveStorySnapshot } from "@/app/lib/system/local-storage";
import { GameManager } from "@/app/lib/game-state/game-manager";
import { Path } from "@/app/lib/routing/path";
import * as Util from "@/app/lib/system/util";
import UI from "@/app/lib/ui/ui";
import { fetchPyramidPuzzle } from "@/app/lib/system/fetchPuzzle";
import { StoryGameState, StorySnapshot } from "@/app/lib/game-state/story-game-state";
import { InternalRouter } from "@/app/lib/routing/internal-router";
import { GameMode } from "@/app/lib/game-state/game-types";

/* We enforce a minimum amount of delay before settig the UI to isLoaded
  The time is neglibible but allows the load animation to play in full,
  instead of getting cut off by the initial page load. This feels better,
  it turns out.
*/
const MIN_LOAD_DELAY = 100;
type MaybeCleanup = void | (() => void)

export function useInitialLoad(
    initialPath: Path | null,
    gameManager: GameManager,
    ui: UI
) {
    function scheduleUIsetLoaded(mode: GameMode, delay: number): () => void {
        const id = setTimeout(() => {
            ui.setLoaded(mode, true);
        }, delay);

        return () => {
            clearTimeout(id);
        }
    }

    function runWhenVisible(
        callback: () => MaybeCleanup // callback either returns void or a cleanup that returns void
    ) : () => void { // returns its own cleanup, BUT WON'T RETURN VOID
        let callbackCleanup: MaybeCleanup;

        /*
        // THIS DOCUMENT FEATURE IS EXPERIMENTAL, REMOVING TO AVOID UNEXPECTED BEHAVIOR AS IT CURRENTLY DOESN'T WORK
        // This accounts for prerendering on webkit (safari, ios, etc.)
        // we will cast as any because TS don't know shit about this yet
        const docAny = document as any;
        if (typeof docAny.prerendering === 'boolean' && docAny.prerendering === true) {
            const onPrerenderingChange = () => {
                if (docAny.prerendering === false) {
                    callbackCleanup = callback();
                }
            }

            document.addEventListener('prerenderingchange', onPrerenderingChange, {once: true});

            // return cleanup, including callback cleanup
            return () => {
                document.removeEventListener('prerenderingchange', onPrerenderingChange);
                callbackCleanup?.();
            }
        }
        */

        // This accounts for being in another tab, or prerendering on chromium desktop
        if (document.visibilityState === 'hidden') {
            const onVisibilityChange = () => {
                if (document.visibilityState === 'visible') {
                    callbackCleanup = callback();
                }
            };

            document.addEventListener('visibilitychange', onVisibilityChange, { once: true });

            // return cleanup, including callback cleanup
            return () => {
                document.removeEventListener('visibilitychange', onVisibilityChange);
                callbackCleanup?.()
            };
        }

        // If we're here, we're already visible, just run
        callbackCleanup = callback();

        // Down here, we have no listener to cleanup
        return () => {
            callbackCleanup?.();
        }
    }

    // LOAD THE PYRAMID PUZZLE
    useEffect(() => {

        // our cleanup function will be set below, if at all
        let cleanup: () => void;
        // tracks whether the fetch is cancelled (prevents unmount race conditions)
        let cancelledFetch = false;

        const snapshot = retreivePyramidSnapshot(gameManager.versions.pyramid)
        if (
            snapshot && snapshot.completed === false && snapshot.seedWord // last conditional is a safety check
        ) {
            Util.devLog(`Retrieved ${gameManager.versions.pyramid} game from local storage.`)
            gameManager.pyramid.update(pyramid => {
                pyramid.importSnapshot(snapshot)
            })
            // if and when visible, schedule the ui load
            cleanup = runWhenVisible(() => scheduleUIsetLoaded('pyramid', MIN_LOAD_DELAY));
        }
        else {
            Util.devLog('Fetching new puzzle on initial load.');
            (async () => {
                const fetched = await fetchPyramidPuzzle(
                    gameManager,
                    ui
                );
                if (!cancelledFetch && fetched) {
                    // schedule the ui load
                    cleanup = runWhenVisible(() => scheduleUIsetLoaded('pyramid', MIN_LOAD_DELAY));
                }
            })() // <-- don't forget to call it!
        }

        return () => {
            cancelledFetch = true;
            cleanup?.();
        }

    }, []);

    // LOAD THE STORY MAP AND MOVE TO THE FIRST LEVEL
    useEffect(() => {
        // we iterate through all levels in the full story
        for (const levelData of gameManager.levels.fullStory.levelDatas) {
            // attempt to retrieve the snapshot
            const levelSnapshot: StorySnapshot | null 
                = retrieveStorySnapshot(levelData.levelID);
            // if it validates, load it
            if (
                levelSnapshot
                && StoryGameState.validateSnapshot(
                    levelSnapshot,
                    gameManager.levels.fullStory.getLevelByID(levelSnapshot.levelID)
                )
            ) {
                gameManager.levels.snapshotMap.set(
                    levelData.levelID,
                    levelSnapshot
                );
                Util.devLog(`loaded level ${levelData.levelID} into map`)
            }
        }
        // attempt to get the last visited level
        const lastVisitedLevelID: string | null = retrieveLastVisitedLevelID();
        if (
            !lastVisitedLevelID // no saved value
            || gameManager.levels.fullStory.getLevelByID(lastVisitedLevelID)
                === null // no corresponding level
        ) {
            // default to level 1
            gameManager.levels.changeLevel(1);
        } else {
            // otherwise move to the last visited level ID
            gameManager.levels.move.byID(lastVisitedLevelID);
        }
        // MINOR DELAY to ensure pretty animation
        setTimeout(() => {
            ui.setLoaded('story', true);    
        }, MIN_LOAD_DELAY);
    }, []);

    // LOAD THE HISTORY
    useEffect(() => {
        const userHistory = retrieveHistory();
        ui.importHistory(userHistory);
    }, []);

    // LOAD THE INITIAL PATH
    useEffect(() => {
        const iRouter = new InternalRouter(gameManager, ui);

        const domain = typeof window !== "undefined"
            ? window.location.origin
            : "";

        if (
            domain === 'https://www.playwordsalad.com/'
            || domain === 'https://www.playwordsalad.com'

        ) {
            iRouter.goPath('/', false);
            return;
        }

        function getLastVisitedLevelPath() : Path | null {
            const lastVisitedLevelID = retrieveLastVisitedLevelID();
            if (!lastVisitedLevelID) {
                return null;
            }
            const levelData = gameManager.levels.fullStory.getLevelByID(lastVisitedLevelID);
            if (!levelData) {
                return null;
            }
            return iRouter.getStoryPathFromLevelName(levelData.levelName);
        }
0
        if (initialPath) {
            // the last visited level is not yet in state
            if (initialPath === '/story') {
                const lastVisitedLevelPath = getLastVisitedLevelPath();
                if (lastVisitedLevelPath) {
                    initialPath = lastVisitedLevelPath;
                }
            }
            iRouter.goPath(initialPath, false)
            /*
                Note that we call goPath with addState = false on this initial route.
                This prevents a bug where, when hitting "back" to return to the initial page load,
                the browser adds a state to history and refreshes the history stack, preventing
                the ability to go forward again through the stack.
            */
        }

    }, []);
}


